import {
	EnvironmentEmailSettingsSaveModel
} from "client/Api/Email/EnvironmentSettings/environmentEmailSettings";
import {
	ReactElement,
	useState
} from "react";
import {
	Checkbox,
	Form,
	FormItem,
	ValidationError,
	AlertBar,
	FontStylePicker,
	RadioButtonGroup,
	RadioButtonGroupItem
} from "client/Components";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import { IFormHandler } from "client/Forms";
import { SignatureConstraints } from "client/Api/Email/Signatures/Personal/personalSignatureSettings";
import { ManagedSignatureForm } from "./ManagedSignatureForm";

export type EnvironmentEmailPageProps = {
	attachable: IFormHandler<EnvironmentEmailSettingsSaveModel>;
	signatureConstraints: SignatureConstraints | undefined;
	isManagedSignatureAvailable: boolean;
};

export const EnvironmentEmailForm = (props: EnvironmentEmailPageProps): ReactElement => {
	const {
		attachable,
		signatureConstraints,
		isManagedSignatureAvailable
	} = props;

	const [
		isShowSenderImagesAlertHidden,
		setIsShowSenderImagesAlertHidden
	] = useState<boolean>(false);

	const shouldRenderShowSenderImagesAlert: boolean = !isShowSenderImagesAlertHidden
		&& attachable
			.value
			.automaticallyShowSenderImages;

	return (
		<Form
			onSubmit="NONE"
			hasWidthConstrained
		>
			{isManagedSignatureAvailable && (
				<SettingsPage.Section title={localize("_ManagedSignature")}>
					<FormItem>
						<Checkbox
							{...attachable.for("isManagedSignatureEnabled").attachToCheckbox()}
							text={localize("_EnableManagedSignature")}
						/>
						<ValidationError
							{...attachable.for("isManagedSignatureEnabled").attachToValidationError()}
						/>
					</FormItem>
					{attachable
						.when(
							x => x.isManagedSignatureEnabled === true,
							attachable =>
								attachable
									.for("managedSignature")
									.ensureIsSet(
										(handler) => {
											return (
												<ManagedSignatureForm
													attachable={handler}
													signatureConstraints={signatureConstraints}
												/>
											);
										}
									)
						)
					}
				</SettingsPage.Section>
			)}
			<SettingsPage.Section title={localize("_MessageFormat")}>
				<FormItem
					label={localize("_DefaultFontStyle")}
				>
					<FontStylePicker
						{...attachable.for("defaultComposerFontStyle")
							.attachToFontStylePicker()}
					>
						<p>{localize("_DefaultComposerFontStyleExplanation_Part1")}</p>
						<p>{localize("_DefaultComposerFontStyleExplanation_Part2")}</p>
					</FontStylePicker>
					<ValidationError
						{...attachable.for("defaultComposerFontStyle")
							.attachToValidationError()}
					/>
				</FormItem>
			</SettingsPage.Section>
			<SettingsPage.Section title={localize("_SenderImages")}>
				<FormItem
					label={localize("_DefaultVisibilityOfSenderImages")}
					isForGroup
				>
					<RadioButtonGroup
						{...attachable.for("automaticallyShowSenderImages")
							.attachToRadioButtonGroup()
						}
						options={showSenderImagesOptions}
					/>
					<ValidationError
						{...attachable.for("automaticallyShowSenderImages")
							.attachToValidationError()}
					/>
				</FormItem>
				{shouldRenderShowSenderImagesAlert && (
					<AlertBar
						kind="warning"
						onDismiss={() => setIsShowSenderImagesAlertHidden(true)}
						isInline
					>
						{localize("_ShowSenderImagesWarningMessage")}
					</AlertBar>
				)}
			</SettingsPage.Section>
		</Form>
	);
};
EnvironmentEmailForm.displayName = "EnvironmentEmailForm";

const showSenderImagesOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_HideSenderImages"),
		value: false
	},
	{
		text: localize("_ShowSenderImages"),
		value: true
	}
];